const ENDPOINT = 'https://bt-newsletter-form.by-association-only.workers.dev/signup';
const ITI_BASE = 'https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build';

/** @type {Promise<any> | null} */
let intlTelInputPromise = null;

function loadIntlTelInput() {
  if (window.intlTelInput) return Promise.resolve(window.intlTelInput);

  intlTelInputPromise ??= new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `${ITI_BASE}/css/intlTelInput.css`;
    document.head.append(link);

    const script = document.createElement('script');
    script.src = `${ITI_BASE}/js/intlTelInput.min.js`;
    script.async = true;
    script.onload = () => resolve(window.intlTelInput);
    script.onerror = reject;
    document.head.append(script);
  });

  return intlTelInputPromise;
}

/**
 * Waitlist / early access signup → Bloomreach newsletter worker.
 */
class BtSignupForm extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {HTMLInputElement | null} */
  #phone = null;
  /** @type {any} */
  #iti = null;
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#form = this.querySelector('form');
    if (!this.#form) return;

    this.#abortController = new AbortController();
    this.#form.addEventListener('submit', this.#onSubmit, { signal: this.#abortController.signal });

    this.#phone = this.querySelector('[data-bt-signup-phone]');
    if (this.#phone) this.#initPhone();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#iti?.destroy?.();
    this.#iti = null;
  }

  async #initPhone() {
    try {
      const intlTelInput = await loadIntlTelInput();
      if (!this.isConnected || !this.#phone || this.#iti) return;

      const country = String(window.Shopify?.country || 'gb').toLowerCase();
      this.#iti = intlTelInput(this.#phone, {
        utilsScript: `${ITI_BASE}/js/utils.js`,
        separateDialCode: true,
        showFlags: false,
        initialCountry: country,
      });
    } catch (error) {
      console.warn('[bt-signup-form] Phone input enhancement unavailable', error);
    }
  }

  #phoneNumber() {
    if (!this.#phone) return '';
    const raw = this.#phone.value.trim();
    if (!raw) return '';
    return this.#iti?.getNumber?.() || raw;
  }

  #payload() {
    const value = (/** @type {string} */ selector) => {
      const input = this.querySelector(selector);
      return input instanceof HTMLInputElement ? input.value.trim() : '';
    };
    const sms = this.querySelector('[data-bt-signup-sms]');
    const smsConsent = sms instanceof HTMLInputElement && sms.checked;

    return {
      firstName: value('[data-bt-signup-firstname]'),
      lastName: value('[data-bt-signup-lastname]'),
      email: value('[data-bt-signup-email]'),
      phone: smsConsent ? this.#phoneNumber() : '',
      sms_consent: smsConsent,
      source: this.#form?.dataset.source || window.location.pathname,
      store: this.#form?.dataset.store || '',
    };
  }

  #onSubmit = async (/** @type {SubmitEvent} */ event) => {
    event.preventDefault();
    const form = this.#form;
    if (!form || !form.reportValidity()) return;

    const submit = form.querySelector('[data-bt-signup-submit]');
    submit?.setAttribute('aria-busy', 'true');
    this.#clearError();

    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.#payload()),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        this.#showSuccess();
      } else {
        this.#showError(data.error || data.message);
      }
    } catch (error) {
      console.error('[bt-signup-form] Submission failed', error);
      this.#showError();
    } finally {
      submit?.removeAttribute('aria-busy');
    }
  };

  #showSuccess() {
    const success = this.querySelector('[data-bt-signup-success]');
    if (success) {
      success.textContent = this.dataset.successMessage || '';
      success.hidden = false;
    }
    this.#form?.reset();
    if (this.#form) this.#form.hidden = true;
  }

  #clearError() {
    const error = this.querySelector('[data-bt-signup-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = '';
    error.hidden = true;
  }

  #showError(/** @type {string} */ message = '') {
    const error = this.querySelector('[data-bt-signup-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message || this.dataset.errorMessage || '';
    error.hidden = false;
  }
}

if (!customElements.get('bt-signup-form')) {
  customElements.define('bt-signup-form', BtSignupForm);
}
